
import { defineComponent } from 'vue';
import Multiselect from 'vue-multiselect';
import Pagination from '@/shared/components/AppPagination.vue';
import AdOfferListItem from '@/ad/components/AdOfferListItem.vue';
import { adService } from '../AdService';
import { accountService } from '@/account/AccountService';
import { OfferListMode } from '../models/OfferListMode';
import { mapGetters, mapState } from 'vuex';
import { GlobalState } from '@/shared/models/GlobalState';
import { estateAgencyService } from '@/estate-agency/EstateAgencyService';
import { scrollToAnchor } from '@/shared/helpers/scrollToAnchor';
import { googleTagManagerService } from '@/shared/services/GoogleTagManagerService';

export default defineComponent({
  components: {
    Multiselect,
    Pagination,
    AdOfferListItem,
  },
  props: {
    mode: Number,
    estateAgencyId: Number,
    frontUserId: Number,
  },
  data() {
    return {
      isLoading: true,
      isEmailConfirmed: false,
      isFilterExtended: true,
      adTypes: [] as null | any[],
      estateTypes: [],
      estateAgents: [],
      status: null,
      pagedList: {
        data: [],
        page: 1,
        pageSize: 20,
        currentCount: 0,
        totalCount: 0,
      },
      queryParams: {
        page: 1,
        pageSize: 20,
        sortBy: 'createdUtc desc',
        isPublished: null as null | boolean,
        estateAgencyId: null as null | undefined | number,
        frontUserId: null as null | undefined | number,
        type: null as null | any,
        estateFrontSubTypes: [] as any[],
        searchTerm: null as null | string,
        location: null as null | string,
        priceFrom: null as null | number,
        priceTo: null as null | number,
        isFavorite: false,
        isHistory: false,
      },
      sortBy: {},
      sortByTypes: [],
    };
  },
  watch: {
    sortBy: {
      handler: function (val) {
        this.queryParams.sortBy = val.value;
      },
    },
    status: {
      handler: function () {
        this.search();
      },
    },
    queryParams: {
      handler: function () {
        this.search();
      },
      deep: true,
    },
  },
  computed: {
    ...mapGetters(['isMobile']),
    ...mapState({
      user: (state) => (state as GlobalState).user,
    }),
    isUserList(): boolean {
      return this.mode === OfferListMode.userlist;
    },
    isFavorite(): boolean {
      return this.mode === OfferListMode.favorite;
    },
    isHistory(): boolean {
      return this.mode === OfferListMode.history;
    },
    isEstateAgentList(): boolean {
      return this.mode === OfferListMode.estateagent;
    },
    isDeveloperProjectList(): boolean {
      return this.mode === OfferListMode.developerproject;
    },
    showFilter(): boolean {
      return !this.isHistory && !this.isDeveloperProjectList;
    },
    isPublic(): boolean {
      return (
        this.mode === OfferListMode.estateagency ||
        this.mode === OfferListMode.estateagent ||
        this.mode === OfferListMode.favorite ||
        this.mode === OfferListMode.history
      );
    },
    showFormHeading(): boolean {
      return this.mode === OfferListMode.estateagency || this.mode === OfferListMode.estateagent;
    },
  },
  async created() {
    try {
      this.sortByTypes = [
        { value: 'createdUtc desc', title: this.$t('AdOfferList.sort-types.created-desc'), icon: 'icon-sort-desc' },
        { value: 'createdUtc', title: this.$t('AdOfferList.sort-types.created'), icon: 'icon-sort-asc' },
      ];
      this.sortBy = this.sortByTypes[0];
      this.isFilterExtended = !this.isMobile;

      if (!this.isPublic) {
        this.isEmailConfirmed = (await accountService.getProfile()).data.isEmailConfirmed;
      }

      const result = await adService.getFilter();
      if (!this.adTypes) this.adTypes = [];
      this.adTypes.push({ key: null, value: 'Všetky' });
      this.adTypes.push(...result.data.adTypes);
      this.estateTypes = result.data.estateTypes;

      if (this.mode == OfferListMode.myestateagency) {
        if (this.$route.query.frontUserId) {
          this.queryParams.frontUserId = +this.$route.query.frontUserId;
        }
        this.estateAgents = (await estateAgencyService.getProfileEstateAgents()).data.data;
      }

      if (this.isFavorite) {
        googleTagManagerService.send({
          event: 'oblubene_inzeraty',
        });
      } else if (this.isHistory) {
        googleTagManagerService.send({
          event: 'historia_prehliadania',
        });
      }
    } catch (error) {
      console.error(error);
    }
    await this.search();

    scrollToAnchor(this.$route.hash);
  },
  methods: {
    filterToggle(value: boolean) {
      this.isFilterExtended = value;
    },
    pageChangeHandle(value: number) {
      this.queryParams.page = value;
    },
    async search() {
      try {
        this.isLoading = true;

        const params = Object.assign({}, this.queryParams);

        if (this.status === 'published') {
          params.isPublished = true;
        } else if (this.status === 'unpublished') {
          params.isPublished = false;
          params.isArchived = false;
        } else if (this.status === 'archived') {
          params.isArchived = true;
        } else {
          params.isArchived = false;
        }

        if (params.type && params.type.key) {
          params.type = params.type.key;
        }
        if (params.estateFrontSubTypes && params.estateFrontSubTypes.length > 0) {
          params.estateFrontSubTypes = params.estateFrontSubTypes.map((x) => x.key);
        }

        if (this.mode === OfferListMode.myestateagency) {
          this.pagedList = (await adService.getMyAgencyList(params)).data;
        } else if (this.mode === OfferListMode.estateagency) {
          params.estateAgencyId = this.estateAgencyId;
          this.pagedList = (await adService.get(params)).data;
        } else if (this.mode === OfferListMode.estateagent) {
          params.frontUserId = this.frontUserId;
          this.pagedList = (await adService.get(params)).data;
        } else if (this.mode === OfferListMode.developerproject) {
          params.frontUserId = this.frontUserId;
          this.pagedList = (await adService.getDeveloperProjects(params)).data;
        } else if (this.mode === OfferListMode.favorite) {
          params.isFavorite = true;
          this.pagedList = (await adService.get(params)).data;
        } else if (this.mode === OfferListMode.history) {
          params.isHistory = true;
          this.pagedList = (await adService.get(params)).data;
        } else {
          this.pagedList = (await adService.getUserList(params)).data;
        }
      } catch (error) {
        //this.handleApiError(error);
      } finally {
        this.isLoading = false;
      }
    },
    async onPublishChange(ad: any) {
      try {
        this.isLoading = true;
        if (!ad.isPublished) {
          await adService.publish(ad.adId);
          ad.publishDate = new Date();
          ad.lastChangedDate = new Date();
        } else {
          await adService.unpublish(ad.adId);
          ad.lastChangedDate = new Date();
        }
        ad.isPublished = !ad.isPublished;
      } catch (error) {
        console.error(error);
        //this.handleApiError(error);
      } finally {
        this.isLoading = false;
      }
    },
    async onReservedChanged(ad: any) {
      try {
        this.isLoading = true;
        if (!ad.isReserved) {
          await adService.reserve(ad.adId);
          ad.lastChangedDate = new Date();
        } else {
          await adService.unreserve(ad.adId);
          ad.lastChangedDate = new Date();
        }
        ad.isReserved = !ad.isReserved;
      } catch (error) {
        console.error(error);
        //this.handleApiError(error);
      } finally {
        this.isLoading = false;
      }
    },
    async onArchivedChanged(ad: any) {
      try {
        this.isLoading = true;
        if (!ad.isArchived) {
          await adService.archive(ad.adId);
          ad.lastChangedDate = new Date();
        } else {
          await adService.unarchive(ad.adId);
          ad.lastChangedDate = new Date();
        }
        ad.isArchived = !ad.isArchived;
      } catch (error) {
        console.error(error);
        //this.handleApiError(error);
      } finally {
        this.isLoading = false;
      }
    },
  },
});
